import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { Button, Modal } from 'ui-toolkit-tailwind/src/components'

import HoldToConfirmButton from './HoldToConfirmButton'

const compactFormatter = (value, timezone) => {
	if (!value) return 'Not recorded'

	const parts = new Intl.DateTimeFormat('en-US', {
		month: 'numeric',
		day: 'numeric',
		year: '2-digit',
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit',
		hour12: true,
		timeZone: timezone || undefined
	}).formatToParts(new Date(value))

	const map = Object.fromEntries(parts.map(part => [part.type, part.value]))

	return `${map.month}/${map.day}/${map.year} ${map.hour}:${map.minute}:${map.second} ${map.dayPeriod}`
}

const timezoneAbbreviation = timezone => {
	try {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: timezone || undefined,
			timeZoneName: 'short'
		}).formatToParts(new Date())

		return parts.find(part => part.type === 'timeZoneName')?.value || 'CDT'
	} catch {
		return 'CDT'
	}
}

const latestRunLabel = status => {
	switch (status) {
	case 'success':
		return 'Succeeded'
	case 'failed':
		return 'Failed'
	default:
		return 'Not recorded'
	}
}

const detailRows = details => [
	{
		label: 'Last PASS (Scheduled)',
		value: compactFormatter(details?.lastSuccessfulScheduledPullAt, details?.timezone)
	},
	{
		label: 'Next Scheduled Pull',
		value: compactFormatter(details?.nextScheduledPullAt, details?.timezone)
	},
	{
		label: 'Most Recent (Scheduled)',
		value: latestRunLabel(details?.latestScheduledRun?.status)
	}
]

const ManualSgDataPullModal = ({ visible, details, activeAction, onClose, onUpdate, onUpdateAndNotify }) => (
	<Modal
		visible={visible}
		onClose={activeAction ? () => {} : onClose}
		size="2xl"
		panelClassName="sm:max-w-[40rem]"
		clickOutsideToClose={!activeAction}
	>
		<Modal.Header>Update VGT Data</Modal.Header>
		<Modal.Body>
			<div className="space-y-4">
				<p className="text-sm text-gray-600">
					This manual SG data pull runs independently and does not modify the existing scheduled portal sync.
					Times displayed are in {timezoneAbbreviation(details?.timezone)}.
				</p>

				<div className="space-y-2 rounded-xl border border-sky-200 bg-sky-50 p-4">
					{detailRows(details).map(row => (
						<div key={row.label} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
							<p className="text-xs font-semibold uppercase tracking-wide text-sky-900 sm:whitespace-nowrap">
								{row.label}
							</p>
							<p className="text-sm text-gray-700 sm:ml-6 sm:whitespace-nowrap sm:text-right">{row.value}</p>
						</div>
					))}

					{details?.latestScheduledRun?.status === 'failed' && (
						<div className="border-t border-sky-200 pt-3">
							<p className="text-xs font-semibold uppercase tracking-wide text-sky-900">Failure reason</p>
							<p className="mt-1 text-sm text-gray-700">
								{details.latestScheduledRun.failureReason || 'No failure reason was recorded.'}
							</p>
						</div>
					)}
				</div>

				<p className="text-xs text-gray-500">
					Update Data &amp; Notify Admin will update the SG data and notify the system administrator of any
					system issues.
				</p>
			</div>
		</Modal.Body>
		<Modal.Footer>
			<div className="flex flex-col-reverse gap-3 px-4 py-5 sm:flex-row sm:items-start sm:justify-end sm:px-6">
				<Button
					type="button"
					intent="secondary"
					onClick={onClose}
					disabled={!!activeAction}
					className="inline-flex w-full items-center justify-center px-4 py-2 text-sm sm:w-[11.5rem]"
				>
					Cancel
				</Button>
				<HoldToConfirmButton
					id="manual-sg-update"
					label="Update Data"
					loadingLabel="Updating..."
					holdLabel="Keep Holding..."
					helperText="Press and hold to update."
					holdingHelperText="Keep holding to update the VGT data."
					busy={activeAction === 'update'}
					disabled={!!activeAction && activeAction !== 'update'}
					onConfirm={onUpdate}
					className="min-h-[38px] bg-[#3E63F4] text-white hover:bg-[#0b2986] focus-visible:outline-[#3E63F4]"
					progressClassName="bg-[#0b2986]/90"
					icon={ArrowDownTrayIcon}
					wrapperClassName="sm:w-[11.5rem]"
				/>
				<HoldToConfirmButton
					id="manual-sg-update-notify"
					label="Update Data & Notify Admin"
					loadingLabel="Updating..."
					holdLabel="Keep Holding..."
					helperText="Press and hold to update and notify."
					holdingHelperText="Keep holding to update the VGT data and notify the administrator."
					busy={activeAction === 'update_and_notify'}
					disabled={!!activeAction && activeAction !== 'update_and_notify'}
					onConfirm={onUpdateAndNotify}
					className="min-h-[38px] bg-[#0ea62d] text-white hover:bg-[#05721b] focus-visible:outline-[#0ea62d]"
					progressClassName="bg-[#05721b]/90"
					icon={ArrowDownTrayIcon}
					wrapperClassName="sm:w-[11.5rem]"
				/>
			</div>
		</Modal.Footer>
	</Modal>
)

ManualSgDataPullModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	details: PropTypes.shape({
		timezone: PropTypes.string,
		lastSuccessfulScheduledPullAt: PropTypes.string,
		nextScheduledPullAt: PropTypes.string,
		latestScheduledRun: PropTypes.shape({
			status: PropTypes.string,
			failureReason: PropTypes.string
		})
	}),
	activeAction: PropTypes.oneOf([null, 'update', 'update_and_notify']),
	onClose: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onUpdateAndNotify: PropTypes.func.isRequired
}

ManualSgDataPullModal.defaultProps = {
	details: null
}

export default ManualSgDataPullModal
